import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  FileText,
  Search,
  GitCompare,
  ListChecks,
  CreditCard,
  CheckCircle2,
  Shield,
  TrendingUp,
  Users,
  ArrowRight,
  Sparkles,
  Bot,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import RadiusNetworkVisualization from "@/components/marketing/RadiusNetworkVisualization";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Radius",
  "url": "https://useradius.io",
  "description": "B2B Procurement Management Platform for brand materials production",
  "logo": "https://useradius.io/logo.png"
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Radius",
  "url": "https://useradius.io",
  "description": "Connect with qualified vendors, get competitive quotes, and manage production of your brand materials."
};

export default function Landing() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard AFTER the page renders
  // This ensures Googlebot (which doesn't authenticate) always sees the full landing page content
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Radius - B2B Procurement Management Platform"
        description="Connect with qualified vendors, get competitive quotes, and manage production of your brand materials. Print, packaging, promotional, and more."
        canonical="/"
        structuredData={[organizationSchema, websiteSchema]}
      />
      <PublicHeader />

      <main>
      {/* Hero Section with Background Visualization */}
      <section className="relative container py-20 md:py-32 overflow-hidden min-h-[400px] md:min-h-[500px] flex items-center">
        {/* Background visualization - full bleed, fixed height to allow cropping */}
        <div className="absolute top-[calc(60%-32px)] left-1/2 -translate-x-1/2 -translate-y-1/2 w-screen h-[800px]">
          <RadiusNetworkVisualization />
        </div>
        
        {/* Semi-transparent overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/70 pointer-events-none" />
        
        {/* Hero content - on top of visualization */}
        <div className="relative z-10 mx-auto max-w-4xl text-center space-y-8">
          <h1 
            className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl"
            style={{
              textShadow: '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            Where Projects Meet Partners
          </h1>
          <p 
            className="text-xl text-foreground max-w-2xl mx-auto font-medium mt-6"
            style={{
              textShadow: '0 0 15px rgba(255, 255, 255, 0.7), 0 0 30px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3), 0 3px 6px rgba(0, 0, 0, 0.2)'
            }}
          >
            Connect with a diverse vendor network, get competitive quotes, and manage production of your brand materials. All in one platform.
          </p>
          <p 
            className="text-base text-foreground/90 max-w-3xl mx-auto mt-4"
            style={{
              textShadow: '0 0 15px rgba(255, 255, 255, 0.7), 0 0 30px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3), 0 3px 6px rgba(0, 0, 0, 0.2)'
            }}
          >
            Print • Packaging • Promotional • Signage • Apparel • Trade Shows • Direct Mail
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {user ? (
              <>
                <Button size="lg" asChild className="shadow-lg">
                  <Link to="/for-clients">
                    I'm a Buyer
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="shadow-lg bg-background/80 backdrop-blur">
                  <Link to="/for-vendors">
                    I'm a Vendor
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <Button size="lg" asChild className="shadow-lg">
                  <Link to="/for-clients">
                    I'm a Buyer
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="shadow-lg bg-background/80 backdrop-blur">
                  <Link to="/for-vendors">
                    I'm a Vendor
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y bg-muted/50 py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">Compare Bids</div>
              <div className="text-2xl font-bold text-primary">and Reduce Costs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">Keep Projects Moving</div>
              <div className="text-2xl font-bold text-primary">on Schedule</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">Find the Right</div>
              <div className="text-2xl font-bold text-primary">Vendors, Faster</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">Maintain Complete Visibility and Control</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Everything You Need</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Comprehensive tools for managing the entire procurement lifecycle from RFQ to delivery.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            icon={Sparkles}
            title="AI Specification Builder"
            description="Describe your project in plain English—our AI creates professional specifications through guided conversation."
          />
          <FeatureCard
            icon={FileText}
            title="RFQ Management"
            description="Create detailed RFQs with specifications, quantities, and delivery requirements."
          />
          <FeatureCard
            icon={Search}
            title="Vendor Discovery"
            description="Browse verified vendors with platform ratings and transaction history."
          />
          <FeatureCard
            icon={GitCompare}
            title="Bid Comparison"
            description="Compare vendor bids side-by-side with line-item breakdowns."
          />
          <FeatureCard
            icon={ListChecks}
            title="Milestone Tracking"
            description="Break projects into milestones with automated tracking and payments."
          />
          <FeatureCard
            icon={CreditCard}
            title="Secure Payments"
            description="Stripe-powered payments with milestone-based releases and purchase protection."
          />
          <FeatureCard
            icon={CheckCircle2}
            title="Delivery Verification"
            description="Submit and review proof of delivery with customizable templates."
          />
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gradient-to-b from-background via-muted/30 to-background py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl font-bold">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple, secure process for both clients and vendors
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Buyer Journey */}
            <div className="relative">
              <div className="sticky top-8 space-y-6 bg-card border rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Users className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold">For Buyers</h3>
                </div>
                <div className="space-y-5">
                  {[
                    { icon: Bot, text: "Build specs with AI-guided conversation" },
                    { icon: Search, text: "Invite or discover verified vendors" },
                    { icon: GitCompare, text: "Review and compare competitive bids" },
                    { icon: CheckCircle2, text: "Award job and set up secure payment" },
                    { icon: ListChecks, text: "Track milestones and progress" },
                    { icon: CreditCard, text: "Verify delivery and release payments" },
                  ].map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div key={index} className="flex gap-4 group hover:translate-x-1 transition-transform duration-200">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground font-semibold shadow-sm group-hover:shadow-md transition-shadow">
                          {index + 1}
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-start gap-3">
                            <IconComponent className="h-5 w-5 text-primary mt-0.5 opacity-70" />
                            <p className="text-sm leading-relaxed">{step.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button asChild className="w-full mt-6">
                  <Link to="/how-it-works/clients">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Vendor Journey */}
            <div className="relative">
              <div className="sticky top-8 space-y-6 bg-card border rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <TrendingUp className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold">For Vendors</h3>
                </div>
                <div className="space-y-5">
                  {[
                    { icon: Shield, text: "Create profile with certifications" },
                    { icon: Search, text: "Browse relevant RFQ opportunities" },
                    { icon: FileText, text: "Submit competitive bids" },
                    { icon: ListChecks, text: "Manage awarded projects" },
                    { icon: CheckCircle2, text: "Complete milestones on schedule" },
                    { icon: CreditCard, text: "Receive secure payments" },
                  ].map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div key={index} className="flex gap-4 group hover:translate-x-1 transition-transform duration-200">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground font-semibold shadow-sm group-hover:shadow-md transition-shadow">
                          {index + 1}
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-start gap-3">
                            <IconComponent className="h-5 w-5 text-primary mt-0.5 opacity-70" />
                            <p className="text-sm leading-relaxed">{step.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button asChild className="w-full mt-6">
                  <Link to="/how-it-works/vendors">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="container py-20">
        <div className="mx-auto max-w-3xl text-center space-y-6">
          <Shield className="h-16 w-16 text-primary mx-auto" />
          <h2 className="text-3xl font-bold">Built on Trust & Security</h2>
          <p className="text-muted-foreground">
            Our platform provides secure transactions with Stripe-powered payments and comprehensive delivery verification. Vendor profiles include verified enrollment credentials, platform-generated ratings and reviews, and transaction history—giving you real performance data to inform your decisions.
          </p>
          <Link to="/verification" className="text-primary hover:underline inline-flex items-center gap-1">
            Learn how verification works <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join thousands of clients and vendors streamlining their procurement process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!user && (
              <>
                <Button size="lg" asChild>
                  <Link to="/auth/signup?type=buyer">
                    Sign Up as Buyer
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/auth/signup?type=vendor">
                    Sign Up as Vendor
                  </Link>
                </Button>
              </>
            )}
            {user && (
              <>
                <Button size="lg" asChild>
                  <Link to="/for-clients">
                    I'm a Buyer
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/for-vendors">
                    I'm a Vendor
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      </main>

      <PublicFooter />
    </div>
  );
}
